import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import ErrorPage from 'next/error'
import { path } from 'ramda'
import React from 'react'

import { PublicGroupWrapper } from '../../../../components/Learning/PublicGroupWrapper'
import PublicLearningLibraryPage from '../../../../components/Learning/PublicLearningLibraryPage'
import { getPublicLearningLibrary } from '../../../../graphql/learning/hooks'
import { withResourceContext } from '../../../../hoc/with-resource-context'
import { requireUser } from '../../../../hoc/with-user'
import { runGetRequest } from '../../../../modules/api/hooks'
import { ResourceResponse } from '../../../../modules/api/types'
import * as types from '../../../../modules/entities/types'
import LearningSeriesListPage from '../../../../routes/groups/components/entity/learning-series-list'
import EntityPage from '../../../../routes/groups/components/entity/page'
import { withEntity } from '../../../../routes/groups/components/entity/with-entity'
import { BasicGroupEntry } from '../../../../types/groups'
import Loading from '../../../../ui/loading'

const PageWrapperComponent = (PageComponent, WrappedComponent) => {
  const GroupPage = props => {
    if (props.isLoading || !path(['resource', 'target'], props)) {
      return (
        <div className="page__content">
          <Loading />
        </div>
      )
    }

    if (!path(['resource', 'target', 'learning', 'url'], props)) {
      return <ErrorPage statusCode={404} />
    }

    return (
      <PageComponent {...props}>
        <WrappedComponent {...props} />
      </PageComponent>
    )
  }

  return withEntity(types.GROUP, GroupPage)
}

export const wrapGroupLearningPageComponent = WrappedComponent => {
  const EntityGroupPage = PageWrapperComponent(EntityPage, WrappedComponent)
  const EntityPublicGroupPage = PageWrapperComponent(
    PublicGroupWrapper,
    WrappedComponent
  )

  return withResourceContext(
    requireUser(
      props => (
        <EntityGroupPage {...props} parentRoute="/learning/groups/[id]" />
      ),
      props => (
        <EntityPublicGroupPage {...props} parentRoute="/learning/groups/[id]" />
      )
    )
  )
}

export const getServerSideProps = async ({ locale, req, query }) => {
  const authed = !!req.cookies['authed']

  const { data: library } = authed
    ? { data: null }
    : await getPublicLearningLibrary(query.id)

  const res = authed
    ? { data: null }
    : await runGetRequest<ResourceResponse<BasicGroupEntry>>(
        `/api/profile/feeds/${encodeURIComponent(
          'group'
        )}/public/${encodeURIComponent(
          query.id
        )}?connections_per_page=1000&include=connections%2Cfounders%2Cadmins%2C-updates`
      )

  const resource = res.data?.success
    ? BasicGroupEntry.check(res.data?.response)
    : null
  if (resource?.target) {
    resource.target._fullyLoaded = true
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'group',
        'learning',
        'case',
        'feed',
      ])),
      library: library ?? null,
      resource,
    },
  }
}

export default requireUser(
  wrapGroupLearningPageComponent(LearningSeriesListPage),
  wrapGroupLearningPageComponent(PublicLearningLibraryPage)
)
